import React, { useEffect, useState } from "react";
import { meApi, formatApiError } from "@/lib/api";
import { PageHeader, Card } from "./_shared";
import { ShoppingBag, MapPin, Calendar, CheckCircle2 } from "lucide-react";
import { formatDateLong } from "@/lib/date";

function fmt(iso) {
  return iso ? (formatDateLong(iso) || "—") : "—";
}

const STATUS_DEF = {
  new: { label: "Nouvelle", color: "text-amber-300 border-amber-400/40" },
  en_cours: { label: "En cours", color: "text-sky-300 border-sky-400/40" },
  signe: { label: "Signé", color: "text-emerald-300 border-emerald-400/40" },
  termine: { label: "Finalisée", color: "text-white/55 border-white/25" },
};

export default function Commandes() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState("");

  const load = () => meApi.commandes().then(setItems).catch(() => {});
  useEffect(() => { load(); }, []);

  const finaliser = async (id) => {
    if (!window.confirm("Marquer ce devis comme finalisé ? Cela arrêtera les emails de relance automatiques.")) return;
    setBusy(id);
    setErr("");
    try {
      await meApi.finaliserCommande(id);
      await load();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div data-testid="ec-commandes">
      <PageHeader eyebrow="Suivi" title="Mes" italic="commandes." desc="Toutes vos demandes de devis et prestations." />
      <Card>
        {items.length === 0 ? (
          <p className="text-sm text-white/55 italic" data-testid="commandes-empty">
            Aucune demande pour l'instant. <a href="/contact" className="text-[#DCB457] hover:underline">Demandez votre devis</a>.
          </p>
        ) : (
          <ul className="divide-y divide-[#DCB457]/15">
            {items.map((c) => {
              const status = STATUS_DEF[c.status] || STATUS_DEF.new;
              const isFinalised = c.status === "termine";
              return (
                <li key={c.id} className="py-5 first:pt-0 last:pb-0" data-testid={`cmd-${c.id}`}>
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 border border-[#DCB457]/40 flex items-center justify-center text-[#DCB457] shrink-0">
                      <ShoppingBag size={18} strokeWidth={1.3} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <p className="font-serif-luxe text-lg">{c.type_evenement || "Demande de prestation"}</p>
                        <span className={`text-[10px] uppercase tracking-[0.2em] border px-2 py-1 ${status.color}`} data-testid={`cmd-status-${c.id}`}>
                          {status.label}
                        </span>
                      </div>
                      <div className="text-white/60 text-sm mt-2 flex flex-wrap items-center gap-x-5 gap-y-1">
                        <span className="inline-flex items-center gap-1.5">
                          <Calendar size={13} strokeWidth={1.3} /> {fmt(c.date_evenement) || fmt(c.created_at)}
                        </span>
                        {c.lieu && (
                          <span className="inline-flex items-center gap-1.5">
                            <MapPin size={13} strokeWidth={1.3} /> {c.lieu}
                          </span>
                        )}
                        {c.nb_invites && <span className="text-white/50">{c.nb_invites} invités</span>}
                      </div>
                      {c.prestations?.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-1.5">
                          {c.prestations.map((p) => (
                            <span key={p} className="text-[10px] uppercase tracking-[0.18em] text-white/55 border border-[#DCB457]/20 px-2 py-1">{p}</span>
                          ))}
                        </div>
                      )}
                      {!isFinalised ? (
                        <button
                          type="button"
                          onClick={() => finaliser(c.id)}
                          disabled={busy === c.id}
                          data-testid={`finaliser-${c.id}`}
                          className="mt-4 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#DCB457] border border-[#DCB457]/40 px-3 py-1.5 hover:bg-[#DCB457]/10 transition disabled:opacity-50"
                        >
                          <CheckCircle2 size={12} strokeWidth={1.5} />
                          {busy === c.id ? "Validation…" : "Marquer comme finalisé"}
                        </button>
                      ) : (
                        <p className="mt-4 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-emerald-300/80">
                          <CheckCircle2 size={12} strokeWidth={1.6} />
                          Devis finalisé — plus de relance automatique
                        </p>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        {err && <p className="text-xs text-red-300 mt-4">{err}</p>}
      </Card>
    </div>
  );
}
